import { useState, useCallback, useRef, useEffect } from 'react';
import { apiService } from '@/lib/api';
import type { PaymentTarget } from '@/types';
import {
    cardPositionHint,
    ensureReaderWarm,
    didReuseExistingConnection,
    collectAndConfirmTapToPayPayment,
    cancelCollect,
    cleanupTerminal,
    checkTapToPaySupport,
    isCardDeclineError,
    isCollectStageError,
    isFatalTapToPayError,
    isTransientTapToPayError,
    recordTapToPayDiagnostic,
    setTapToPayProgressListener,
    startTapToPayAttempt,
    type TapToPayProgressUpdate,
    type TapToPaySupportResult,
} from '@/lib/tapToPay';

/** A PaymentIntent created ahead of time by the payment screen. */
export interface PrefetchedTerminalIntent {
    /** Id of the job or estimate the intent charges. */
    targetId: string;
    amountCents: number;
    clientSecret: string;
}

// ── State machine ─────────────────────────────────────────────────────────────

export type TapToPayState =
    | 'idle'
    | 'initializing'
    | 'discovering'
    | 'collecting'
    | 'confirming'
    | 'succeeded'
    | 'failed';

const VALID_TRANSITIONS: Record<TapToPayState, TapToPayState[]> = {
    idle:         ['initializing'],
    initializing: ['discovering', 'failed'],
    discovering:  ['collecting', 'failed'],
    collecting:   ['confirming', 'failed', 'idle'],
    confirming:   ['succeeded', 'failed'],
    succeeded:    [],
    failed:       ['idle', 'initializing'],
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseTapToPayReturn {
    state: TapToPayState;
    errorMessage: string | null;
    progress: TapToPayProgressUpdate | null;
    supportResult: TapToPaySupportResult | null;
    startPayment: (
        target: PaymentTarget,
        amountCents: number,
        terminalLocationId?: string,
        prefetchedIntent?: PrefetchedTerminalIntent | null,
    ) => Promise<void>;
    cancel: () => Promise<void>;
    retry: () => Promise<void>;
}

export function useTapToPay(): UseTapToPayReturn {
    const [state, setState] = useState<TapToPayState>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [progress, setProgress] = useState<TapToPayProgressUpdate | null>(null);
    const [supportResult, setSupportResult] = useState<TapToPaySupportResult | null>(null);
    const stateRef = useRef<TapToPayState>('idle');
    const cancelledRef = useRef(false);
    /** Identifies the attempt that currently owns the flow; see `isStale` in startPayment. */
    const attemptIdRef = useRef<string | null>(null);

    // Check support on mount
    useEffect(() => {
        checkTapToPaySupport().then(setSupportResult);
    }, []);

    const handleProgress = useCallback((nextProgress: TapToPayProgressUpdate) => {
        if (!cancelledRef.current) {
            setProgress(nextProgress);
        }
    }, []);

    // Receive progress for the whole time this screen is mounted, including work
    // a background warm started before it opened.
    useEffect(() => {
        setTapToPayProgressListener(handleProgress);
        return () => {
            setTapToPayProgressListener(null);
        };
    }, [handleProgress]);

    // Unmount no longer disconnects: the reader connection is deliberately
    // longer-lived than this component so a warmed reader survives closing the
    // payment screen. Cancelling collect still cancels any in-flight discovery,
    // so a half-finished attempt can't poison the next one. The reset paths that
    // replace the old unmount disconnect are `retry` and the stale-connection
    // recovery in `startPayment` below.
    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            attemptIdRef.current = null;
            cancelCollect().catch(() => {});
        };
    }, []);

    const transitionTo = useCallback((next: TapToPayState, error?: string) => {
        const current = stateRef.current;
        const allowed = VALID_TRANSITIONS[current];
        if (!allowed.includes(next)) {
            recordTapToPayDiagnostic('state', 'Invalid Tap to Pay state transition blocked', {
                current,
                next,
            }, 'warn');
            return;
        }

        recordTapToPayDiagnostic('state', 'Tap to Pay state changed', {
            from: current,
            to: next,
            error: error ?? null,
        });

        stateRef.current = next;
        setState(next);
        setErrorMessage(next === 'failed' && error ? error : null);
    }, []);

    const startPayment = useCallback(async (
        target: PaymentTarget,
        amountCents: number,
        terminalLocationId?: string,
        prefetchedIntent?: PrefetchedTerminalIntent | null,
    ) => {
        if (stateRef.current !== 'idle' && stateRef.current !== 'failed') return;
        cancelledRef.current = false;

        // Exact match only — a prefetched intent is a real charge, so a
        // near-miss on job or amount must never be reused.
        const usableIntent = prefetchedIntent
            && prefetchedIntent.targetId === target.id
            && prefetchedIntent.amountCents === amountCents
            ? prefetchedIntent
            : null;

        const attemptId = startTapToPayAttempt({
            jobId: target.id, // diagnostics label; an estimate id for estimate payments
            amountCents,
            hasTerminalLocationId: Boolean(terminalLocationId),
            usedPrefetchedIntent: Boolean(usableIntent),
        });
        attemptIdRef.current = attemptId;

        /**
         * Whether this invocation still owns the flow. `cancelledRef` alone is
         * not enough: starting a new payment resets it, which would re-arm the
         * error handling of an already-abandoned attempt — a canceled collect
         * would then reach the recovery path below and re-present the reader
         * with a stale client secret.
         */
        const isStale = () => cancelledRef.current || attemptIdRef.current !== attemptId;

        // ── Phase 1: Kick off all setup work in parallel ─────────────────
        // Reader prep (init + discover + connect) and API setup (intent +
        // location) are independent. Running them concurrently means a fresh
        // payment doesn't pay the SDK-init / NFC-warmup tax twice.
        setProgress({
            stage: 'initializing',
            message: 'Initializing Tap to Pay…',
            source: 'local',
        });
        transitionTo('initializing');

        const readerReadyPromise = ensureReaderWarm(terminalLocationId);
        const intentPromise = usableIntent
            ? Promise.resolve({ client_secret: usableIntent.clientSecret })
            : apiService.createTerminalPaymentIntent(target, amountCents / 100);

        // Reader is the long pole; flip into "discovering" after init kicks
        // off so the UI matches what the user sees (the discover/connect
        // progress events from tapToPay.ts will refine it further).
        transitionTo('discovering');

        const [readerResult, intentResult] = await Promise.allSettled([
            readerReadyPromise,
            intentPromise,
        ]);

        if (isStale()) {
            recordTapToPayDiagnostic('cancel', 'Tap to Pay attempt canceled during setup');
            return;
        }

        if (readerResult.status === 'rejected') {
            const msg = readerResult.reason?.message ?? 'Failed to prepare Tap to Pay reader';
            recordTapToPayDiagnostic('reader', 'Reader preparation failed', {
                error: msg,
            }, 'error');
            // Disconnect only if the SDK is in a bad state (attestation /
            // bridge-stalled / etc). Transient errors keep the connection
            // around so the user can retry instantly.
            if (isFatalTapToPayError(msg)) {
                await cleanupTerminal();
            }
            transitionTo('failed', msg);
            return;
        }

        if (intentResult.status === 'rejected') {
            const msg = intentResult.reason?.message ?? 'Failed to create payment intent';
            recordTapToPayDiagnostic('intent', 'Payment intent creation failed', {
                error: msg, amountCents,
            }, 'error');
            transitionTo('failed', msg);
            return;
        }

        const clientSecret = intentResult.value.client_secret;
        // Captured before collect: whether this attempt is riding a reader that
        // was already connected when we got here (a warm reader) rather than one
        // we just established.
        const onWarmConnection = didReuseExistingConnection();

        recordTapToPayDiagnostic('setup', 'Parallel setup completed', {
            hasClientSecret: Boolean(clientSecret),
            onWarmConnection,
        });

        if (isStale()) {
            recordTapToPayDiagnostic('cancel', 'Tap to Pay attempt canceled after setup');
            return;
        }

        const failWith = async (err: any) => {
            if (isStale()) return;
            const msg = err?.message || err?.errorMessage || (typeof err === 'string' ? err : null) || JSON.stringify(err) || 'Payment failed';
            recordTapToPayDiagnostic('confirm', 'Tap to Pay payment flow failed', {
                error: msg,
                fatal: isFatalTapToPayError(msg),
            }, 'error');

            // Only disconnect for unrecoverable errors. Transient ones
            // (canceled tap, card read error, etc) keep the reader
            // connected so the next try is instant.
            if (isFatalTapToPayError(msg)) {
                recordTapToPayDiagnostic('cleanup', 'Disconnecting reader after fatal error', {
                    error: msg,
                });
                await cleanupTerminal();
            }

            transitionTo('failed', msg);
        };

        const succeed = () => {
            recordTapToPayDiagnostic('confirm', 'Tap to Pay payment flow completed successfully');
            if (isStale()) return;
            setProgress({
                stage: 'confirming_payment',
                message: 'Payment confirmed.',
                source: 'local',
            });
            transitionTo('confirming');
            transitionTo('succeeded');
        };

        // ── Phase 2: Collect the tap and confirm the PaymentIntent ───────
        setProgress({
            stage: 'waiting_for_tap',
            message: `Ready for tap. Hold one contactless card ${cardPositionHint()}.`,
            source: 'local',
        });
        transitionTo('collecting');
        try {
            await collectAndConfirmTapToPayPayment(clientSecret);
            succeed();
        } catch (err: any) {
            if (isStale()) return;

            // A reader we warmed earlier can have gone stale while idle, and the
            // first symptom is collect failing with the customer's card already
            // out. Reconnect once and try again rather than surfacing an error:
            // the worst case is the user waits roughly as long as they used to.
            //
            // Only for collect — a confirm failure means the PaymentIntent has
            // already moved on, so replaying it is not safe. And never for a
            // transient error: those mean the reader is fine and the user either
            // backed out or presented the card badly. Re-arming there is how
            // dismissing Apple's Tap to Pay sheet used to make it pop straight
            // back up.
            const message = err?.message ?? '';
            const recoverable = onWarmConnection
                && isCollectStageError(err)
                && !isTransientTapToPayError(message)
                && !isFatalTapToPayError(message);

            if (!recoverable) {
                await failWith(err);
                return;
            }

            recordTapToPayDiagnostic('recover', 'Collect failed on a warm reader; reconnecting once', {
                error: err?.message ?? String(err),
            }, 'warn');
            setProgress({
                stage: 'connecting_reader',
                message: 'Reconnecting the reader…',
                source: 'local',
            });

            try {
                await cleanupTerminal();
                await ensureReaderWarm(terminalLocationId);
                if (isStale()) return;
                setProgress({
                    stage: 'waiting_for_tap',
                    message: `Ready for tap. Hold one contactless card ${cardPositionHint()}.`,
                    source: 'local',
                });
                await collectAndConfirmTapToPayPayment(clientSecret);
                succeed();
            } catch (retryErr: any) {
                await failWith(retryErr);
            }
        }
    }, [transitionTo]);

    const cancel = useCallback(async () => {
        cancelledRef.current = true;
        // Disown the attempt outright. Its collect is about to reject with
        // "The command was canceled", and nothing from it may act on that —
        // not even after a later payment resets `cancelledRef`.
        attemptIdRef.current = null;
        recordTapToPayDiagnostic('cancel', 'Tap to Pay cancel requested by user');

        // Reset the machine BEFORE tearing down the native collect, never after.
        // `cancelCollect` can hang (the iOS SDK has been seen never resolving
        // cancelCollectPaymentMethod when cancel lands moments after the sheet
        // opens), and awaiting first meant the reset below never ran — leaving the
        // state stuck at 'collecting'. `startPayment` only runs from idle/failed,
        // so the whole flow was then dead until the app was relaunched.
        // The attempt is already disowned above, so nothing in flight can act on
        // this reset. The reader stays connected, so reopening is still instant.
        stateRef.current = 'idle';
        setState('idle');
        setErrorMessage(null);
        setProgress(null);

        await cancelCollect();
    }, []);

    const retry = useCallback(async () => {
        if (stateRef.current !== 'failed') return;

        // "Try Again" is now the user's only manual way out of a wedged reader:
        // closing the payment screen no longer disconnects. So for anything that
        // looks reader-related, force a clean disconnect before retrying rather
        // than handing the next attempt the same broken connection.
        //
        // Genuinely transient errors (the customer pulled the card away, a bad
        // read) keep the connection so the retry is still instant.
        // A decline is likewise not a reader fault — it is the issuer's answer, and
        // the reader demonstrably just read a card and round-tripped to Stripe. It
        // used to fall through to the reset below, costing a full disconnect →
        // rediscover → reconnect before every re-tap: measured on Android at ~5.4s
        // of dead time per retry (readyMs 5359-6374, versus 473-902 warm).
        const failure = errorMessage ?? '';
        const needsReset = failure.length > 0
            && !isTransientTapToPayError(failure)
            && !isCardDeclineError(failure);
        recordTapToPayDiagnostic('retry', 'Tap to Pay retry requested', {
            error: failure || null,
            forcingDisconnect: needsReset,
        });
        if (needsReset) {
            await cleanupTerminal().catch(() => {});
        }

        stateRef.current = 'idle';
        setState('idle');
        setErrorMessage(null);
        setProgress(null);
    }, [errorMessage]);

    return { state, errorMessage, progress, supportResult, startPayment, cancel, retry };
}
